/**
 * The list of jobs or tasks.
 *
 * Copyright © Ircam Amplify 2024-2025
 */

import { fetchJobs } from '@/api'
import { SortTableHeader } from '@/components/jobui/SortTableHeader'
import {
  getModuleByServiceName,
  getModuleDisplayName,
} from '@/components/ModuleConfig/ModuleConfigList'
import TimeAnalysisDialog, {
  TimeAnalysisSeries,
} from '@/components/TimeAnalysisChart'
import { logger } from '@/config/logger'
import { getUserState, useSelector } from '@/store'
import { LogTag } from '@amplify3/duplo-back-end'
import { useEffect, useRef, useState } from 'react'
import {
  BiBot,
  BiCoinStack,
  BiHive,
  BiLoaderAlt,
  BiLoaderCircle,
  BiSolidWatch,
  BiTagAlt,
} from 'react-icons/bi'
import JobInfo from './JobInfo'

const JobsList = () => {
  const tableBodyRef = useRef(null)

  const { jobs, lastJobId } = useSelector(getUserState)

  const [filters, setFilters] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [isChartsDialogVisible, setChartsDialogVisible] = useState(false)
  const [timeAnalysisSeries, setTimeAnalysisSeries] = useState<
    TimeAnalysisSeries[]
  >([])

  function showChartsDialogVisible(series: TimeAnalysisSeries[]) {
    setTimeAnalysisSeries(series)
    setChartsDialogVisible(true)
  }

  const modules = [
    ...new Set(
      jobs.flatMap((elt) => getModuleByServiceName(elt?.service_name))
    ),
  ].filter((elt) => elt !== undefined)

  const filteredJobs = jobs.filter((elt) =>
    filters.length > 0
      ? filters.includes(getModuleDisplayName(elt?.service_name))
      : true
  )

  const columnHeaderList = [
    { name: 'Name', icon: <BiTagAlt /> },
    { name: 'Module', icon: <BiHive /> },
    { name: 'Status', icon: <BiLoaderCircle /> },
    { name: 'Credits', icon: <BiCoinStack /> },
    { name: 'Output', icon: <BiBot /> },
  ]

  const onFilterClick = (module: string) => {
    const copy = [...filters]
    const copyIndex = copy.findIndex((elt) => elt === module)
    if (copyIndex > -1) {
      copy.splice(copyIndex, 1)
    } else {
      copy.push(module)
    }
    setFilters(copy)
  }

  const onRefreshClick = async () => {
    if (!refreshing) {
      setRefreshing(true)
      await fetchJobs({ refreshing: true })
      setRefreshing(false)
    }
  }

  useEffect(() => {
    onRefreshClick()
  }, [])

  // Debug logs
  useEffect(() => {
    logger.debug(LogTag.TaskList, 'jobs:', jobs)
  }, [jobs])

  const onLoadMoreClick = async () => {
    if (!loading) {
      setLoading(true)
      await fetchJobs({ refreshing: false })
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="pBtn2"
        onClick={onRefreshClick}
        id="RefreshButton"
      >
        {refreshing ? (
          <h4>
            Loading <BiLoaderAlt className="loadingIcon" />
          </h4>
        ) : (
          'Refresh List'
        )}
      </button>
      {modules.length > 0 && (
        <div className="modulesFilter">
          <div>
            <b>Filters</b>
            <button
              className="pBtn"
              type="button"
              onClick={() => setFilters([])}
            >
              Reset
            </button>
          </div>
          {modules.map((module, index) => (
            <button
              key={`${module.info.displayModuleName}_${index}`}
              className={
                filters.includes(module.info.displayModuleName)
                  ? 'moduleTag'
                  : 'moduleTag notSelected'
              }
              type="button"
              onClick={() => {
                onFilterClick(module.info.displayModuleName)
              }}
            >
              {module.info.displayModuleName}
            </button>
          ))}
        </div>
      )}
      <div className="jobui-table">
        <table>
          <thead className="sticky [inset-block-start:0] bg-white">
            <tr>
              <SortTableHeader name={'Created'} icon={<BiSolidWatch />} />
              {columnHeaderList.map(({ name, icon }) => (
                <th key={name} className="resultCol">
                  <div className="SortTableHeader">
                    {icon}
                    {name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {filteredJobs.length > 0 && (
            <tbody ref={tableBodyRef}>
              {filteredJobs.map((job) => (
                <JobInfo
                  key={job?.job_id}
                  job={job}
                  showChartsDialogVisible={showChartsDialogVisible}
                />
              ))}
            </tbody>
          )}
        </table>
        {jobs.length > 0 && lastJobId !== undefined && (
          <button
            type="button"
            id="LoadMoreButton"
            className="!mt-5 !py-2 !px-4 button-dark !rounded-full"
            disabled={loading}
            onClick={onLoadMoreClick}
          >
            <div className="button-label">
              {loading ? 'Loading...' : 'Load more'}
            </div>
          </button>
        )}
      </div>

      {timeAnalysisSeries.length > 0 && isChartsDialogVisible && (
        <TimeAnalysisDialog
          series={timeAnalysisSeries}
          changeVisible={setChartsDialogVisible}
          isVisible={isChartsDialogVisible}
        />
      )}
    </>
  )
}

export default JobsList
